import { useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { allArticles } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.wiki";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const effectiveSlug = slug || (location.pathname === "/acbuyspreadsheet-guide" ? "acbuyspreadsheet-guide" : undefined);
  const article = allArticles.find((a) => a.slug === effectiveSlug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [effectiveSlug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Article not found</h1>
          <p className="mt-2 text-sm text-foreground-500">The article you are looking for does not exist.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-home-line"></i> Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleUrl = `${siteUrl}${location.pathname}`;

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.metaDescription,
            image: article.imageUrl,
            url: articleUrl,
            datePublished: "2026-06-25",
            dateModified: "2026-06-25",
            author: {
              "@type": "Organization",
              name: "ACBuySpreadsheet",
              url: siteUrl,
            },
            publisher: {
              "@type": "Organization",
              name: "ACBuySpreadsheet",
              url: siteUrl,
              logo: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/acdcfd47829ab306d380ea84eb14e50b.jpeg",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": articleUrl,
            },
          }),
        }}
      />
      {article.faqItems.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: article.faqItems.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      )}
      <Navbar />

      <main>
        <section className="w-full pt-24 md:pt-32 pb-12 md:pb-16 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-start gap-10 md:gap-16">
              <div className="w-full lg:w-1/2">
                <div className="rounded-[2rem] overflow-hidden bg-background-100 lg:sticky lg:top-24">
                  <img
                    src={article.imageUrl}
                    alt={article.imageAlt}
                    title={article.title}
                    className="w-full h-auto"
                  />
                </div>
              </div>

              <article className="w-full lg:w-1/2">
                <nav aria-label="breadcrumb" className="mb-4">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i>
                    ACBuySpreadsheet Home
                  </Link>
                </nav>

                <span className="inline-block px-3 py-1.5 bg-accent-100 text-accent-800 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
                  {article.badge}
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
                  A comprehensive resource from the{" "}
                  <Link
                    to="/"
                    className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2"
                  >
                    ACBuySpreadsheet
                  </Link>{" "}
                  wiki. Explore our complete collection of guides and resources for smarter order management.
                </p>

                <div className="mt-8 md:mt-10 space-y-8 md:space-y-10">
                  {article.sections.map((section, idx) => (
                    <div key={idx}>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                        {section.heading}
                      </h2>
                      <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-3">
                        {section.content.split(". ").filter(Boolean).map((sentence, sIdx) => {
                          const isLast = sIdx === section.content.split(". ").filter(Boolean).length - 1;
                          return <p key={sIdx}>{isLast ? sentence : sentence + "."}</p>;
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {article.faqItems.length > 0 && (
                  <div className="mt-10 md:mt-12 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/60">
                    <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                      Frequently Asked Questions
                    </h3>
                    <div className="space-y-5">
                      {article.faqItems.map((faq, idx) => (
                        <div key={idx}>
                          <h4 className="text-sm md:text-base font-semibold text-foreground-800 mb-1.5">
                            {faq.question}
                          </h4>
                          <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8 flex flex-col sm:flex-row items-start gap-4">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Visit ACBuy Spreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-100 text-foreground-900 rounded-lg text-sm font-semibold hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-home-line"></i>
                    Back to Home
                  </Link>
                </div>

                <div className="mt-8 pt-6 border-t border-background-200/60">
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">
                    More ACBuySpreadsheet Guides
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {allArticles.filter(a => a.slug !== effectiveSlug).slice(0, 5).map((a) => (
                      <Link
                        key={a.slug}
                        to={`/guide/${a.slug}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
                      >
                        {a.title.split(":")[0].split(" (")[0]}
                        <i className="ri-arrow-right-line text-[10px]"></i>
                      </Link>
                    ))}
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}